import { useContext, useState, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  TextField,
  Typography,
  FormHelperText,
  FormControl,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import { useMutation } from "@apollo/client";
import DialogContent from "@mui/material/DialogContent";
//Import from project
import "../../../Style/dialogstyle.scss";
import { AuthContext } from "../../../Context/AuthContext";
import { SelectDatePicker } from "../../../Components/Include/SelectDatePicker/SelectDatePicker";
import {
  SelectClass,
  SelectmultiScheduleByClass,
} from "../../../Components/Include/DynamicSelect/DynamicSelect";
import {
  CREATE_STUDENT_ENROLLMENT,
  UPDATE_STUDENT_ENROLLMENT,
} from "../../../Schema/Class";
import { DialogTitleComp } from "../../../Utils/Component";

export default function AssignClassToStudentForm({
  open,
  editData,
  studentId,
  setRefetch,
  handleClose,
  dialogTitle,
}) {
  // Change Language
  const { language, setAlert, t } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [isNewStudent, setIsNewStudent] = useState(
    editData?.isNewStudent ? editData?.isNewStudent : false,
  );

  const [selecteSchedule, setSelecteSchedule] = useState([]);

  // console.log("editData::", editData);

  const [createStudentEnrollment] = useMutation(CREATE_STUDENT_ENROLLMENT, {
    onCompleted: ({ createStudentEnrollment }) => {
      setLoading(false);
      if (createStudentEnrollment?.isSuccess === true) {
        setAlert(true, "success", createStudentEnrollment?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createStudentEnrollment?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageEn: message, messageKh: message });
    },
  });

  const [updateStudentEnrollment] = useMutation(UPDATE_STUDENT_ENROLLMENT, {
    onCompleted: ({ updateStudentEnrollment }) => {
      setLoading(false);
      if (updateStudentEnrollment?.isSuccess === true) {
        setAlert(true, "success", updateStudentEnrollment?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateStudentEnrollment?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageEn: message, messageKh: message });
    },
  });

  const ValidationSchema = Yup.object().shape({
    remark: Yup.string(),
    totalHour: Yup.number().moreThan(0).required(t("required")),
    classId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    payAs: Yup.string().required(t("required")),
    scheduleId: Yup.string().required(t("required")),
    startSchoolDate: Yup.string().required(t("required")),
    // endHourDate: Yup.string().required(t("required")),
    endHourDate: Yup.date()
      .required(t("required"))
      .test(
        "is-after",
        t("End date must be after start date"),
        function (value) {
          const { startSchoolDate } = this.parent;
          return value && startSchoolDate
            ? moment(value).isAfter(moment(startSchoolDate))
            : true;
        },
      ),
  });

  const formik = useFormik({
    initialValues: {
      classId: { id: "", title: "" },
      scheduleId: "",
      payAs: "month",
      remark: "",
      totalHour: 0,
      startSchoolDate: moment(),
      endHourDate: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: async (values) => {
      // console.log("studentId", studentId, editData);
      setLoading(true);

      if (dialogTitle === "Update") {
        updateStudentEnrollment({
          variables: {
            id: editData?._id,
            input: {
              startSchoolDate: moment(values?.startSchoolDate).format(
                "YYYY-MM-DD",
              ),
              endHourDate: moment(values?.endHourDate).format("YYYY-MM-DD"),
              totalHour: parseFloat(values?.totalHour),
              studentId: editData?.studentId?._id,
              classId: values?.classId?.id,
              payAs: values?.payAs,
              scheduleId: selecteSchedule?.map((e) => e?.id),
              isNewStudent: isNewStudent,
              remark: values?.remark,
            },
          },
        });
      } else {
        createStudentEnrollment({
          variables: {
            input: {
              startSchoolDate: moment(values?.startSchoolDate).format(
                "YYYY-MM-DD",
              ),
              endHourDate: moment(values?.endHourDate).format("YYYY-MM-DD"),
              totalHour: parseFloat(values?.totalHour),
              studentId: studentId,
              classId: values?.classId?.id,
              payAs: values?.payAs,
              scheduleId: selecteSchedule?.map((e) => e?.id),
              isNewStudent: isNewStudent,
              remark: values?.remark,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  // console.log("editData::", editData);

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (selecteSchedule?.length > 0) {
      setFieldValue("scheduleId", "scheduleId");
    } else {
      setFieldValue("scheduleId", "");
    }
  }, [selecteSchedule]);

  useEffect(() => {
    if (editData && dialogTitle === "Update") {
      setFieldValue("remark", editData?.remark);
      setFieldValue("totalHour", editData?.totalHour);
      setFieldValue("payAs", editData?.payAs || "month");
      setFieldValue("startSchoolDate", editData?.startSchoolDate);
      setFieldValue("endHourDate", editData?.endHourDate);
      setFieldValue("classId", {
        id: editData?.classId?._id,
        title:
          language === "en"
            ? editData?.classId?.courseId?.enName +
              " " +
              editData?.classId?.className
            : editData?.classId?.courseId?.enName +
              " " +
              editData?.classId?.className,
      });
    }

    if (editData?.scheduleId?.length > 0) {
      setSelecteSchedule(
        editData?.scheduleId?.map((data) => {
          return {
            id: data?._id,
            title: data?.day + " " + data?.startTime + "-" + data?.endTime,
          };
        }),
      );
    }
  }, [open, editData]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="sm">
      <DialogTitleComp
        title={
          dialogTitle === "Update"
            ? t("modal_title_update_class")
            : t("modal_title_add_class")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={7}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_class")}
                </Typography>
                <SelectClass
                  checkSchedule={true}
                  setSelecteSchedule={setSelecteSchedule}
                  value={values?.classId}
                  onChange={(val) =>
                    setFieldValue("classId", val || { id: "", title: "" })
                  }
                  disabled={dialogTitle === "Update"}
                />
                {!!errors.classId?.id && touched.classId?.id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.classId?.id}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={5}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_pay_as")}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={values?.payAs}
                    onChange={(e) => setFieldValue("payAs", e.target.value)}
                  >
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="course">Course</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_start_school_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.startSchoolDate}
                  onChange={(val) =>
                    setFieldValue("startSchoolDate", val || "")
                  }
                />
                {!!errors.startSchoolDate && touched.startSchoolDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.startSchoolDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_end_school_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.endHourDate}
                  onChange={(val) => setFieldValue("endHourDate", val || "")}
                />
                {!!errors.endHourDate && touched.endHourDate && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.endHourDate}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_total_hour")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  type="number"
                  size="small"
                  className="text-field"
                  placeholder={t("thead_total_hour")}
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        {t("thead_hour")}
                      </Typography>
                    ),
                  }}
                  {...getFieldProps("totalHour")}
                  error={Boolean(touched.totalHour && errors.totalHour)}
                  helperText={touched.totalHour && errors.totalHour}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_new_student")}
                </Typography>
                <Switch
                  checked={isNewStudent}
                  onChange={(e) => setIsNewStudent(e.target.checked)}
                  slotProps={{ input: { "aria-label": "controlled" } }}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_schedule")}
                </Typography>
                <SelectmultiScheduleByClass
                  classId={values?.classId?.id || editData?.classId?._id}
                  value={selecteSchedule}
                  onChange={(data, val) => setSelecteSchedule(val)}
                />
                {!!errors.scheduleId && touched.scheduleId && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.scheduleId}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  rows={2}
                  fullWidth
                  multiline
                  size="small"
                  className="text-field"
                  placeholder={t("label_remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className="btn-create" fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className="btn-create"
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
